import { useEffect, useMemo, useState } from "react";
import { ChartLineUp, Users, Calendar as CalendarIcon, TrendUp, TrendDown, Crown, ChartBar, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../lib/useCurrency";

const periodLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short" });
};

const quarterKey = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-").map(Number);
  return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
};

const quarterLabel = (qKey) => {
  if (!qKey) return "";
  const [y, q] = qKey.split("-");
  return `${q} '${String(y).slice(2)}`;
};

// Group an ordered array of {period: "YYYY-MM", ...numericFields} into quarterly buckets.
function groupByQuarter(rows, fields) {
  const buckets = new Map();
  for (const r of rows) {
    const k = quarterKey(r.period);
    if (!buckets.has(k)) {
      const init = { period: k };
      for (const f of fields) init[f] = 0;
      buckets.set(k, init);
    }
    const b = buckets.get(k);
    for (const f of fields) b[f] += Number(r[f] || 0);
  }
  return Array.from(buckets.values());
}

export default function TrainerAnalytics() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState("month"); // "month" | "quarter"
  const [months, setMonths] = useState(12);
  const [quarters, setQuarters] = useState(4);
  const [exporting, setExporting] = useState(false);

  // Effective fetch range in months
  const effectiveMonths = granularity === "quarter" ? quarters * 3 : months;

  const view = useMemo(() => {
    if (!data) return null;
    if (granularity === "month") {
      return {
        revenueTrend: data.revenue.trend.map((r) => ({ label: periodLabel(r.period), value: r.amount })),
        signupsTrend: data.retention.new_signups.map((r) => ({ label: periodLabel(r.period), value: r.count })),
        sessionsTrend: data.sessions.trend.map((r) => ({ label: periodLabel(r.period), completed: r.completed, scheduled: r.scheduled, cancelled: r.cancelled })),
        thisLabel: "This month",
        lastLabel: "Last month",
        deltaLabel: "vs last month",
        signupsChartLabel: "New sign-ups per month",
        thisValue: data.revenue.this_month,
        lastValue: data.revenue.last_month,
        deltaPct: data.revenue.delta_pct,
      };
    }
    // Quarter granularity
    const revQ = groupByQuarter(data.revenue.trend.map((r) => ({ period: r.period, amount: r.amount })), ["amount"]);
    const sigQ = groupByQuarter(data.retention.new_signups.map((r) => ({ period: r.period, count: r.count })), ["count"]);
    const sessQ = groupByQuarter(data.sessions.trend, ["completed", "scheduled", "cancelled"]);
    const thisQ = revQ.length ? revQ[revQ.length - 1].amount : 0;
    const lastQ = revQ.length >= 2 ? revQ[revQ.length - 2].amount : 0;
    const deltaQ = lastQ > 0 ? ((thisQ - lastQ) / lastQ) * 100 : (thisQ > 0 ? 100 : 0);
    return {
      revenueTrend: revQ.map((r) => ({ label: quarterLabel(r.period), value: r.amount })),
      signupsTrend: sigQ.map((r) => ({ label: quarterLabel(r.period), value: r.count })),
      sessionsTrend: sessQ.map((r) => ({ label: quarterLabel(r.period), completed: r.completed, scheduled: r.scheduled, cancelled: r.cancelled })),
      thisLabel: "This quarter",
      lastLabel: "Last quarter",
      deltaLabel: "vs last quarter",
      signupsChartLabel: "New sign-ups per quarter",
      thisValue: thisQ,
      lastValue: lastQ,
      deltaPct: Math.round(deltaQ * 10) / 10,
    };
  }, [data, granularity]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/analytics/export.csv?months=${effectiveMonths}&granularity=${granularity}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `traina-analytics-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Analytics exported");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/analytics/overview?months=${effectiveMonths}`);
        if (alive) setData(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [effectiveMonths]);

  if (loading && !data) {
    return <div className="text-zinc-500 tracker" data-testid="analytics-loading">Loading analytics…</div>;
  }
  if (!data || !view) return null;

  return (
    <div className="space-y-10" data-testid="trainer-analytics">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-zinc-500 mb-2">[ Analytics ]</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">Numbers.</h1>
          <p className="text-zinc-400 mt-3">{user?.is_owner ? "Org-wide" : "Your"} revenue, retention and session metrics.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-controls">
          <div className="flex items-center" data-testid="granularity-toggle">
            <button
              onClick={() => setGranularity("month")}
              className={`tracker px-3 py-2 border ${granularity === "month" ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
              data-testid="granularity-month"
            >
              Month
            </button>
            <button
              onClick={() => setGranularity("quarter")}
              className={`tracker px-3 py-2 border border-l-0 ${granularity === "quarter" ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
              data-testid="granularity-quarter"
            >
              Quarter
            </button>
          </div>
          {granularity === "month" ? (
            <div className="flex items-center gap-2" data-testid="months-filter">
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={`tracker px-3 py-2 border ${months === m ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
                  data-testid={`months-${m}`}
                >
                  {m} mo
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2" data-testid="quarters-filter">
              {[4, 8].map((q) => (
                <button
                  key={q}
                  onClick={() => setQuarters(q)}
                  className={`tracker px-3 py-2 border ${quarters === q ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
                  data-testid={`quarters-${q}`}
                >
                  {q} Q
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting || loading}
            className="tracker px-3 py-2 border border-white/10 hover:border-brand flex items-center gap-2 disabled:opacity-50"
            data-testid="export-csv"
            title="Export analytics as CSV"
          >
            <DownloadSimple size={14} weight="bold" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Revenue */}
      <Section title="Revenue" icon={<ChartLineUp size={18} weight="duotone" className="text-brand" />}>
        <div className="grid sm:grid-cols-4 gap-4">
          <Stat label={view.thisLabel} value={`${symbol}${view.thisValue.toFixed(2)}`} money />
          <Stat
            label={view.deltaLabel}
            value={`${view.deltaPct >= 0 ? "+" : ""}${view.deltaPct.toFixed(1)}%`}
            accent={view.deltaPct >= 0 ? "text-emerald-400" : "text-[#FF6B61]"}
            icon={view.deltaPct >= 0 ? <TrendUp size={16} weight="bold" /> : <TrendDown size={16} weight="bold" />}
          />
          <Stat label={view.lastLabel} value={`${symbol}${view.lastValue.toFixed(2)}`} money />
          <Stat label="Lifetime" value={`${symbol}${data.revenue.lifetime.toFixed(2)}`} money />
        </div>
        <BarChart
          data={view.revenueTrend}
          accent="var(--brand)"
          formatValue={(v) => `${symbol}${v.toFixed(0)}`}
          testId="revenue-chart"
        />
        <TopClients clients={data.revenue.top_clients} />
      </Section>

      {/* Retention */}
      <Section title="Retention" icon={<Users size={18} weight="duotone" className="text-brand" />}>
        <div className="grid sm:grid-cols-4 gap-4">
          <Stat label="Active clients" value={data.retention.active_clients} accent="text-emerald-400" />
          <Stat label="Churned" value={data.retention.churned_clients} accent={data.retention.churned_clients > 0 ? "text-amber-400" : ""} />
          <Stat label="New (last 30d)" value={data.retention.new_30d} accent="text-brand-light" />
          <Stat label="On monthly plan" value={`${data.retention.plan_adoption_pct}%`} accent="text-emerald-400" />
        </div>
        <BarChart
          data={view.signupsTrend}
          accent="#10B981"
          formatValue={(v) => `${v}`}
          chartLabel={view.signupsChartLabel}
          testId="signups-chart"
        />
      </Section>

      {/* Sessions */}
      <Section title="Session utilization" icon={<CalendarIcon size={18} weight="duotone" className="text-brand" />}>
        <div className="grid sm:grid-cols-4 gap-4">
          <Stat label="Completed" value={data.sessions.totals.completed} accent="text-emerald-400" />
          <Stat label="Cancelled" value={data.sessions.totals.cancelled} accent="text-[#FF6B61]" />
          <Stat label="Completion rate" value={`${data.sessions.completion_rate}%`} accent="text-emerald-400" />
          <Stat label="Cancel rate" value={`${data.sessions.cancel_rate}%`} accent={data.sessions.cancel_rate > 15 ? "text-[#FF6B61]" : ""} />
        </div>
        <StackedBarChart
          data={view.sessionsTrend}
          testId="sessions-chart"
        />
        <SessionTypeBreakdown items={data.sessions.by_type} />
      </Section>

      {/* Coaches (owner only) */}
      {data.coaches && data.coaches.length > 0 && (
        <Section title="Coach breakdown" icon={<ChartBar size={18} weight="duotone" className="text-brand" />}>
          <div className="card-flat overflow-x-auto" data-testid="coach-breakdown">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#0F0F0F]">
                <tr>
                  <Th>Coach</Th>
                  <Th>Revenue</Th>
                  <Th>Sessions (completed / total)</Th>
                  <Th>Completion %</Th>
                </tr>
              </thead>
              <tbody>
                {data.coaches.map((c) => {
                  const pct = c.sessions_total ? Math.round((c.sessions_completed / c.sessions_total) * 100) : 0;
                  return (
                    <tr key={c.trainer_id} className="border-t border-white/5" data-testid={`coach-row-${c.trainer_id}`}>
                      <Td>
                        <div className="flex items-center gap-2">
                          {c.is_owner && <Crown size={12} weight="fill" className="text-[#FFC700]" />}
                          <span className="font-semibold">{c.name}</span>
                        </div>
                      </Td>
                      <Td><span className="font-mono money">${c.revenue.toFixed(2)}</span></Td>
                      <Td><span className="font-mono">{c.sessions_completed} / {c.sessions_total}</span></Td>
                      <Td>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 max-w-[160px] bg-white/5 h-2">
                            <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="font-mono text-xs">{pct}%</span>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, accent, icon, money }) {
  return (
    <div className="card-flat p-5">
      <div className="flex items-center gap-2 tracker text-zinc-500 mb-2">{icon}{label}</div>
      <div className={`font-display text-3xl ${money ? "money" : ""} ${accent || ""}`}>{value}</div>
    </div>
  );
}

function BarChart({ data, accent = "var(--brand)", formatValue = (v) => v, chartLabel, testId }) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.value)), [data]);
  if (data.length === 0) return null;
  return (
    <div className="card-flat p-5" data-testid={testId}>
      {chartLabel && <div className="tracker text-zinc-500 mb-4">{chartLabel}</div>}
      <div className="flex items-end gap-2 h-44">
        {data.map((d, i) => {
          const h = max > 0 ? (d.value / max) * 100 : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center group">
              <div className="flex-1 w-full flex items-end relative">
                <div
                  className="w-full transition-all duration-300 group-hover:opacity-80"
                  style={{ height: `${h}%`, background: accent, minHeight: d.value > 0 ? "2px" : 0 }}
                />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-mono whitespace-nowrap bg-[#141414] border border-white/10 px-1.5 py-0.5">
                  <span className="money">{formatValue(d.value)}</span>
                </div>
              </div>
              <div className="tracker mt-2 text-[9px]">{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackedBarChart({ data, testId }) {
  const max = useMemo(
    () => Math.max(1, ...data.map((d) => d.completed + d.scheduled + d.cancelled)),
    [data]
  );
  return (
    <div className="card-flat p-5" data-testid={testId}>
      <div className="flex items-center gap-4 tracker text-zinc-500 mb-4">
        <Legend color="#10B981" label="Completed" />
        <Legend color="var(--brand-light)" label="Scheduled" />
        <Legend color="#FF6B61" label="Cancelled" />
      </div>
      <div className="flex items-end gap-2 h-44">
        {data.map((d, i) => {
          const total = d.completed + d.scheduled + d.cancelled;
          const totalH = max > 0 ? (total / max) * 100 : 0;
          const compPct = total ? (d.completed / total) * 100 : 0;
          const schedPct = total ? (d.scheduled / total) * 100 : 0;
          const cancPct = total ? (d.cancelled / total) * 100 : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center group">
              <div className="flex-1 w-full flex items-end relative">
                <div className="w-full flex flex-col-reverse" style={{ height: `${totalH}%` }}>
                  <div style={{ height: `${compPct}%`, background: "#10B981" }} />
                  <div style={{ height: `${schedPct}%`, background: "var(--brand-light)" }} />
                  <div style={{ height: `${cancPct}%`, background: "#FF6B61" }} />
                </div>
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-mono whitespace-nowrap bg-[#141414] border border-white/10 px-1.5 py-0.5">
                  {d.completed}✓ {d.scheduled}· {d.cancelled}×
                </div>
              </div>
              <div className="tracker mt-2 text-[9px]">{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-3 h-3 inline-block" style={{ background: color }} />
      {label}
    </span>
  );
}

function TopClients({ clients }) {
  if (!clients.length) {
    return <div className="card-flat p-8 text-center text-zinc-500 tracker">No paid invoices yet</div>;
  }
  const max = clients[0].amount || 1;
  return (
    <div className="card-flat" data-testid="top-clients">
      <div className="px-5 py-3 border-b border-white/10 tracker text-zinc-500 flex items-center justify-between">
        <span>Top clients by revenue</span>
        <span className="text-[10px]">{clients.length}</span>
      </div>
      {clients.map((c, i) => (
        <div key={c.client_id} className="px-5 py-3 border-b border-white/5 last:border-b-0 flex items-center gap-4" data-testid={`top-client-${i}`}>
          <div className="font-mono text-zinc-500 w-6">{i + 1}.</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{c.name}</div>
            <div className="font-mono text-[10px] text-zinc-500 truncate">{c.email}</div>
          </div>
          <div className="flex-1 max-w-[200px] hidden sm:block">
            <div className="bg-white/5 h-2"><div className="h-full bg-brand" style={{ width: `${(c.amount / max) * 100}%` }} /></div>
          </div>
          <div className="font-mono font-semibold money text-right min-w-[100px]">${c.amount.toFixed(2)}</div>
        </div>
      ))}
    </div>
  );
}

function SessionTypeBreakdown({ items }) {
  const total = items.reduce((s, i) => s + i.count, 0);
  if (total === 0) return null;
  return (
    <div className="card-flat" data-testid="session-type-breakdown">
      <div className="px-5 py-3 border-b border-white/10 tracker text-zinc-500">Session-type mix</div>
      {items.map((it) => {
        const pct = total ? (it.count / total) * 100 : 0;
        return (
          <div key={it.type} className="px-5 py-3 border-b border-white/5 last:border-b-0 flex items-center gap-4">
            <div className="font-semibold w-32 sm:w-40 truncate">{it.type}</div>
            <div className="flex-1 bg-white/5 h-2"><div className="h-full bg-brand-light" style={{ width: `${pct}%` }} /></div>
            <div className="font-mono text-xs w-20 text-right">{it.count} · {pct.toFixed(0)}%</div>
          </div>
        );
      })}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 tracker text-zinc-500 font-normal">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-middle">{children}</td>; }
